/**
 * Records that the user asked to sign out, so the embedded auth-page recovery
 * ({@code FusionAuthRecovery}) can tell a deliberate sign-out apart from a CCS session that merely
 * dropped while the Fusion host's JWT stayed valid.
 *
 * Both cases land on the same {@code /auth} route, and recovery re-establishes the session from the
 * host's JWT — which, after a sign-out, silently signed the user straight back in.
 *
 * The flag lives in {@code sessionStorage} because signing out navigates the frame, so it has to
 * survive a full page load, and it must not leak to other tabs. It is one-shot: whoever reads it
 * clears it.
 */
const FUSION_SIGN_OUT_REQUESTED_KEY = 'akixiFusionSignOutRequested'

/**
 * Marks the current frame as having requested a sign-out. Call before issuing the logout request so
 * the flag is already in place however quickly the frame navigates afterwards.
 */
export const markFusionSignOutRequested = (): void => {
    try {
        window.sessionStorage.setItem(FUSION_SIGN_OUT_REQUESTED_KEY, 'true')
    } catch {
        // sessionStorage unavailable — recovery suppression degrades gracefully.
    }
}

/**
 * Reports whether a sign-out was requested in this frame, clearing the flag so it applies once.
 *
 * @return {@code true} when the user asked to sign out, so no session recovery should be attempted.
 */
export const consumeFusionSignOutRequested = (): boolean => {
    try {
        const requested: OnlyNullable<string> = window.sessionStorage.getItem(FUSION_SIGN_OUT_REQUESTED_KEY)

        if (requested === null) return false

        window.sessionStorage.removeItem(FUSION_SIGN_OUT_REQUESTED_KEY)

        return true
    } catch {
        return false
    }
}
